import type { App } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

import { createAuthGuard } from '@auth0/auth0-vue'

import {
	PROJECT_SETTINGS_ROUTES,
	SINGLE_PROJECT_ROUTES_NAMES
} from './singleProjectTypes'

export function getSingleProjectRoutes(app?: App): RouteRecordRaw {
	return {
		path: '/project/:projectId',
		name: SINGLE_PROJECT_ROUTES_NAMES.SingleProject,
		component: () => import('~/portal/pages/single-project/index.vue'),
		beforeEnter: createAuthGuard(app as any),
		children: [
			{
				path: 'documents-checklist',
				name: SINGLE_PROJECT_ROUTES_NAMES.PlaintCertificateChecklist,
				component: () =>
					import('~/portal/pages/single-project/tabs/documents-checklist.vue')
			},
			{
				path: 'confirmity-check-checklist',
				name: SINGLE_PROJECT_ROUTES_NAMES.ConfirmityCheckChecklist,
				component: () =>
					import('~/portal/pages/single-project/tabs/documents-checklist.vue')
			},
			{
				path: 'internal-checklist',
				name: SINGLE_PROJECT_ROUTES_NAMES.InternalChecklist,
				component: () =>
					import('~/portal/pages/single-project/tabs/documents-checklist.vue')
			},
			{
				path: 'business-documentation',
				name: SINGLE_PROJECT_ROUTES_NAMES.BusinessDocumentation,
				component: () =>
					import(
						'~/portal/pages/single-project/tabs/business-documentation.vue'
					)
			},
			{
				path: 'technical-documentation',
				name: SINGLE_PROJECT_ROUTES_NAMES.TechnicalDocumentation,
				component: () =>
					import(
						'~/portal/pages/single-project/tabs/technical-documentation.vue'
					)
			},
			{
				path: 'general-information',
				name: SINGLE_PROJECT_ROUTES_NAMES.GeneralInformation,
				component: () =>
					import('~/portal/pages/single-project/tabs/general-information.vue')
			},
			{
				path: 'chat',
				name: SINGLE_PROJECT_ROUTES_NAMES.CHAT,
				component: () => import('~/portal/pages/single-project/tabs/chat.vue')
			},
			{
				path: 'activity-logs',
				name: SINGLE_PROJECT_ROUTES_NAMES.ActivityLogs,
				component: () =>
					import('~/portal/pages/single-project/tabs/activity-logs.vue')
			},
			{
				path: 'calculations',
				name: SINGLE_PROJECT_ROUTES_NAMES.Calculations,
				component: () =>
					import('~/portal/pages/single-project/tabs/calculations.vue')
			}
		]
	}
}

export const getSingleProjectInvoicePdfViewerRoute = (
	app?: App
): RouteRecordRaw => {
	return {
		path: '/project/:projectId/invoice/pdf-viewer/:file/:name/:id/',
		name: SINGLE_PROJECT_ROUTES_NAMES.PdfViewerInvoice,
		component: () =>
			import('~/portal/pages/single-project/invoice-pdf-viewer.vue'),
		beforeEnter: createAuthGuard(app as any),
		props: route => ({
			file: route.params.file,
			name: route.params.name,
			id: route.params.id,
			idProject: route.params.projectId
		})
	}
}

export const getSingleProjectOfferPdfViewerRoute = (
	app?: App
): RouteRecordRaw => {
	return {
		path: '/project/:projectId/offer/pdf-viewer/:file/:name/:id/',
		name: SINGLE_PROJECT_ROUTES_NAMES.PdfViewerOffer,
		component: () =>
			import('~/portal/pages/single-project/offer-pdf-viewer.vue'),
		beforeEnter: createAuthGuard(app as any),
		props: route => ({
			file: route.params.file,
			name: route.params.name,
			id: +route.params.id!,
			idProject: +route.params.projectId!
		})
	}
}

export const getSingleProjectCertificatePdfViewerRoute = (
	app?: App
): RouteRecordRaw => {
	return {
		path: '/project/:projectId/certificate/pdf-viewer/:file/:name/:certificateId/',
		name: SINGLE_PROJECT_ROUTES_NAMES.PdfViewerCertificate,
		component: () =>
			import('~/portal/pages/single-project/certificate-pdf-viewer.vue'),
		beforeEnter: createAuthGuard(app as any)
	}
}

export const getSingleProjectReportPdfViewerRoute = (
	app?: App
): RouteRecordRaw => {
	return {
		path: '/project/:projectId/report/pdf-viewer/:file/:name/:id/',
		name: SINGLE_PROJECT_ROUTES_NAMES.PdfViewerReport,
		component: () =>
			import('~/portal/pages/single-project/report-pdf-viewer.vue'),
		beforeEnter: createAuthGuard(app as any),
		props: route => ({
			file: route.params.file,
			name: route.params.name,
			id: +route.params.id!,
			idProject: +route.params.projectId!
		})
	}
}

export const getSingleProjectOtherDocumentPdfViewerRoute = (
	app?: App
): RouteRecordRaw => {
	return {
		path: '/project/:projectId/other-document/pdf-viewer/:file/:name/:id/',
		name: SINGLE_PROJECT_ROUTES_NAMES.PdfViewerOtherDocument,
		component: () =>
			import('~/portal/pages/single-project/other-documents-pdf-viewr.vue'),
		beforeEnter: createAuthGuard(app as any),
		props: route => ({
			file: route.params.file,
			name: route.params.name,
			id: +route.params.id!,
			idProject: +route.params.projectId!
		})
	}
}
export const getSingleProjectProductPage = (app?: App): RouteRecordRaw => {
	return {
		path: '/project/:projectId/products',
		name: SINGLE_PROJECT_ROUTES_NAMES.Products,
		component: () => import('~/portal/pages/single-project/products.vue'),
		beforeEnter: createAuthGuard(app as any)
	}
}

export const getProjectSettingsRoute = (app?: App): RouteRecordRaw[] => {
	return [
		{
			path: '/project/:projectId/settings',
			name: SINGLE_PROJECT_ROUTES_NAMES.ProjectSettings,
			component: () =>
				import('~/portal/pages/single-project/project-settings.vue'),
			beforeEnter: createAuthGuard(app as any),
			children: [
				{
					path: '',
					redirect: { name: PROJECT_SETTINGS_ROUTES.General }
				},
				{
					path: 'general',
					name: PROJECT_SETTINGS_ROUTES.General,
					component: () =>
						import(
							'~/portal/pages/single-project/project-settings/general-settings.vue'
						)
				},
				{
					path: 'email-reminders',
					name: PROJECT_SETTINGS_ROUTES.EmailReminders,
					component: () =>
						import(
							'~/portal/pages/single-project/project-settings/email-reminders.vue'
						)
				}
			]
		},
		{
			path: '/project/:projectId/settings/email-reminders/add',
			name: PROJECT_SETTINGS_ROUTES.AddEmailReminder,
			component: () =>
				import(
					'~/portal/pages/single-project/project-settings/email-reminders/add-edit/add-edit-reminder.vue'
				)
		},
		{
			path: '/project/:projectId/settings/email-reminders/:reminderId/edit',
			name: PROJECT_SETTINGS_ROUTES.EditEmailReminder,
			component: () =>
				import(
					'~/portal/pages/single-project/project-settings/email-reminders/add-edit/add-edit-reminder.vue'
				),
			props: route => ({ reminderId: route.params.reminderId })
		}
	]
}
